import { gpsDriveDelete, gpsDriveGet, gpsDrivePost, gpsDrivePut } from "@/lib/api/gpsdrive-client";

// "Registrar Assistência" (PWA, menu Perfil, permissão
// administracao.assistencia) + "Configurar Assistência" (Administração →
// Comunicação, mesma permissão) — ver AssistanceController no
// gpsdrive-backend. "Minhas Assistências" (myRequests) é self-scoped, sem
// permissão nenhuma, mesmo padrão de gpsDriveBillingService#myStatus.
//
// Regras INDIVIDUAIS por serviço (pedido 2026-09-11) — substituem a
// antiga regra global única (assistanceDedupeDays/getSettings/
// updateSettings, removidas) por annualLimit/minIntervalDays em cada
// ServiceType, com bloqueio de verdade no backend (não mais só um aviso
// visual) — ver getUsage/POST requests.
export interface ServiceType {
  id: string;
  name: string;
  active: boolean;
  annualLimit: number;
  minIntervalDays: number;
  groupId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceTypeInput {
  name: string;
  annualLimit: number;
  minIntervalDays: number;
  groupId?: string | null;
}

export interface UpdateServiceTypeInput {
  name?: string;
  active?: boolean;
  annualLimit?: number;
  minIntervalDays?: number;
  groupId?: string | null;
}

// Grupo de assistência (pedido 2026-09-11), ex.: "Assistência 100km"
// agrupando vários ServiceType — organiza a tela Configurar Assistência
// em seções e é o que um Plano "com assistência" vincula (ver
// gpsdrive-plans.service.ts#serviceGroupId).
export interface ServiceGroup {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertServiceGroupInput {
  name: string;
}

export type BillingSummaryStatus = "em_dia" | "atraso" | "sem_pendencias" | "nao_sincronizado";

export interface ClientSummary {
  userId: string;
  name: string;
  billing: {
    status: BillingSummaryStatus;
    nextDueDate: string | null;
    nextDueValue: number | null;
  };
}

export type UsageBlockedReason = "annual_limit" | "min_interval" | null;

// Uso ATUAL de um serviço pra um (cliente, veículo) — alimenta o badge
// "1 de 3 utilizações" e o bloqueio proativo no Registrar Assistência.
// canRequest:false já reflete o mesmo cálculo que o backend também roda
// de novo, de verdade, dentro de POST /requests — nunca é só um aviso.
export interface ServiceUsage {
  serviceTypeId: string;
  usageCount: number;
  annualLimit: number;
  minIntervalDays: number;
  lastRequestedAt: string | null;
  nextAvailableAt: string | null;
  canRequest: boolean;
  blockedReason: UsageBlockedReason;
}

export interface CreateAssistanceRequestInput {
  traccarUserId: number;
  deviceId: number;
  serviceTypeId: string;
  reason?: string | null;
  // Guincho (2026-10-16): Ponto B — para onde o veículo vai.
  destinationAddress?: string | null;
  destinationLat?: number | null;
  destinationLng?: number | null;
}

export interface AssistanceRequest {
  id: string;
  serviceTypeId: string;
  serviceTypeName: string;
  traccarDeviceId: number;
  vehicleName: string;
  reason: string | null;
  requestedAt: string;
  requestedByName: string;
}

export const gpsDriveAssistanceService = {
  listServiceTypes: () => gpsDriveGet<ServiceType[]>("/api/v1/assistance/service-types"),
  createServiceType: (input: CreateServiceTypeInput) =>
    gpsDrivePost<ServiceType>("/api/v1/assistance/service-types", input),
  updateServiceType: (id: string, data: UpdateServiceTypeInput) =>
    gpsDrivePut<ServiceType>(`/api/v1/assistance/service-types/${id}`, data),

  listServiceGroups: () => gpsDriveGet<ServiceGroup[]>("/api/v1/assistance/service-groups"),
  createServiceGroup: (input: UpsertServiceGroupInput) =>
    gpsDrivePost<ServiceGroup>("/api/v1/assistance/service-groups", input),
  updateServiceGroup: (id: string, input: UpsertServiceGroupInput) =>
    gpsDrivePut<ServiceGroup>(`/api/v1/assistance/service-groups/${id}`, input),
  deleteServiceGroup: (id: string) => gpsDriveDelete<void>(`/api/v1/assistance/service-groups/${id}`),

  getClientSummary: (traccarUserId: number) =>
    gpsDriveGet<ClientSummary>(`/api/v1/assistance/clients/${traccarUserId}/summary`),

  getUsage: (traccarUserId: number, deviceId: number, serviceTypeId: string) =>
    gpsDriveGet<ServiceUsage>(
      `/api/v1/assistance/requests/usage?traccarUserId=${traccarUserId}&deviceId=${deviceId}&serviceTypeId=${serviceTypeId}`
    ),

  createRequest: (input: CreateAssistanceRequestInput) =>
    gpsDrivePost<AssistanceRequest>("/api/v1/assistance/requests", input),

  myRequests: () => gpsDriveGet<AssistanceRequest[]>("/api/v1/assistance/me/requests"),
};
